import { useState } from "react";

import { type ApolloError, type MutationFunction, useLazyQuery, useMutation } from "@apollo/client";

import { type HostedLens } from "../../gql-generated/types";
import { deleteHostedLens } from "../api/mutation/deleteHostedLens";
import {
    type DeleteHostedLensMutation,
    type DeleteHostedLensMutationVariables,
} from "../api/mutation/deleteHostedLens.gql-generated";
import { generateHostedLens } from "../api/mutation/generateHostedLens";
import {
    type GenerateHostedLensMutation,
    type GenerateHostedLensMutationVariables,
} from "../api/mutation/generateHostedLens.gql-generated";
import { updateHostedLens } from "../api/mutation/updateHostedLens";
import {
    type UpdateHostedLensMutation,
    type UpdateHostedLensMutationVariables,
} from "../api/mutation/updateHostedLens.gql-generated";
import { getHostedLens } from "../api/query/getHostedLens";
import { type GetHostedLensQuery, type GetHostedLensQueryVariables } from "../api/query/getHostedLens.gql-generated";
import { getHostedLensList } from "../api/query/getHostedLensList";
import {
    type GetHostedLensListQuery,
    type GetHostedLensListQueryVariables,
} from "../api/query/getHostedLensList.gql-generated";

export type HostedLensListItem = {
    name?: string;
    iconUrl?: string;
} & Partial<HostedLens>;

type HostedLensType =
    | GetHostedLensQuery["getHostedLens"]["lens"]
    | UpdateHostedLensMutation["updateHostedLensStatus"]["lens"]
    | GenerateHostedLensMutation["generateHostedLens"]["lens"];

interface HostedLensHook {
    createHostedLens: MutationFunction<GenerateHostedLensMutation, GenerateHostedLensMutationVariables>;
    updateHostedLensStatus: MutationFunction<UpdateHostedLensMutation, UpdateHostedLensMutationVariables>;
    fetchHostedLens: MutationFunction<GetHostedLensQuery, GetHostedLensQueryVariables>;
    deleteHostedLensLink: MutationFunction<DeleteHostedLensMutation, DeleteHostedLensMutationVariables>;
    fetchHostedLensList: MutationFunction<GetHostedLensListQuery, GetHostedLensListQueryVariables>;
    hostedLens: HostedLensType;
    hostedLensList: readonly HostedLensListItem[];
    isLoading: boolean;
    isUpdating: boolean;
    error?: ApolloError;
}

export const useHostedLens = (): HostedLensHook => {
    const [hostedLens, setHostedLens] = useState<HostedLensType>(null);

    const [fetchHostedLens, { loading, error }] = useLazyQuery<GetHostedLensQuery, GetHostedLensQueryVariables>(
        getHostedLens,
        {
            errorPolicy: "all",
            onCompleted: (data) => {
                setHostedLens(data.getHostedLens.lens);
            },
        },
    );

    const [fetchHostedLensList, { data: hostedLensList, loading: hostedLensListLoading }] = useLazyQuery<
        GetHostedLensListQuery,
        GetHostedLensListQueryVariables
    >(getHostedLensList, {
        fetchPolicy: "network-only",
    });

    const [updateHostedLensStatus, { loading: isUpdating, error: updateError }] = useMutation<
        UpdateHostedLensMutation,
        UpdateHostedLensMutationVariables
    >(updateHostedLens, {
        onCompleted: (data) => {
            setHostedLens(data.updateHostedLensStatus.lens);
        },
    });

    const [createHostedLens, { loading: isGenerating, error: generationError }] = useMutation<
        GenerateHostedLensMutation,
        GenerateHostedLensMutationVariables
    >(generateHostedLens, {
        onCompleted: (data) => {
            setHostedLens(data.generateHostedLens.lens);
        },
    });

    const [deleteHostedLensLink, { loading: isDeleting, error: deleteError }] = useMutation<
        DeleteHostedLensMutation,
        DeleteHostedLensMutationVariables
    >(deleteHostedLens, {
        onCompleted: () => {
            setHostedLens(null);
        },
    });

    return {
        createHostedLens,
        updateHostedLensStatus,
        fetchHostedLens,
        deleteHostedLensLink,
        fetchHostedLensList,
        hostedLens,
        hostedLensList: hostedLensList?.getHostedLenses.lensesList ?? [],
        isLoading: loading || hostedLensListLoading,
        isUpdating: isUpdating || isGenerating || isDeleting,
        error: error ?? updateError ?? generationError ?? deleteError,
    };
};
